import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import isMobile from "is-mobile";
import { toast } from "react-toastify";
import { Add, ViewListOutlined } from "@mui/icons-material";
import { Page } from "../../../components/page-base";
import { DatagridResponsive } from "../../../components/datagrid-base";
import { TDatagridData, TDGKey } from "../../../components/datagrid-base/types";
import { Input } from "../../../components/inputs";
import { FormRow } from "../../../components/datagrid-base/components/container/styles";
import { ContentConditional } from "@/components/content-conditional";
import { HomeForm, ScenarioContainer } from "./styles";
import { AccountsService } from "../services";
import {
  EditAccountResponse,
  TAccount,
  TAccountResponse,
  TDeleteAccountResponse
} from "../models";
import { cnpj, cpf } from "cpf-cnpj-validator";
import theme from "../../../styles/theme";
import permissionsStore from "../../../store/permissions";
import { useDebounce } from "@/utils/useDebounce";
import { useNavigationContext } from "@/contexts";
import { TokensDisplay } from "@/components/tokens";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";

export const Accounts: React.FC = () => {
  /* ===== Hooks, searchParams e estados ===== */
  const [searchParams, setSearchParams] = useSearchParams();
  const { setSelectedAccountCombo, rowsPerPage } = useNavigationContext();
  const { role, scenarioId, setScenarioId } = permissionsStore();
  const { debounce } = useDebounce();

  const busca = useMemo(() => searchParams.get("busca") || "", [searchParams]);

  const [count, setCount] = useState<number>(0);
  const [accounts, setAccounts] = useState<TAccount[]>([]);
  const [tab, setTab] = useState<number>(1);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [document, setDocument] = useState<string>("");
  const [isDocValid, setIsDocValid] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      name: "",
      id: 0
    }
  });

  /* ===== Funções de Navegação ===== */
  const navigateTable = (newPage: string) => {
    setPage(Number(newPage));
  };

  /* ===== Funções de Validação ===== */
  const isValidDocument = (length: number) =>
    document.length === length && !document.includes("_");
  const alertInvalidDoc = () => {
    const isCompany = isValidDocument(18);
    if (isCompany && !isDocValid) {
      toast.error("Documento inválido.");
    }
  };

  /* ===== Função de Busca de Dados ===== */
  const fetchData = async () => {
    const result = await AccountsService.getAccounts(page, busca, rowsPerPage);
    if (result.success) {
      setCount(result.meta!.total);
      if (page === 1) {
        setAccounts(result.data!);
      } else {
        if (result.meta!.total > 0 && result.data?.length === 0) {
          navigateTable("1");
        }
        setAccounts((prev) => [...prev, ...result.data!]);
      }
    } else {
      setAccounts([]);
      setCount(0);
      toast.error(result.message);
    }
  };

  /* ===== useEffects ===== */
  // Busca de dados e validação de documento quando page ou busca mudam
  useEffect(() => {
    fetchData();
    alertInvalidDoc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, busca, rowsPerPage]);

  // Validação de documento em alterações
  useEffect(() => {
    alertInvalidDoc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document]);

  /* ===== Funções de Formulário ===== */
  const onSubmit = async (payload: TDatagridData) => {
    let response: TAccountResponse | EditAccountResponse | null = null;
    if (isEdit) {
      response = (await AccountsService.editAccount(
        payload.id.toString(),
        payload.name
      )) as EditAccountResponse;
    } else {
      response = (await AccountsService.createAccount(
        payload.name,
        document
      )) as TAccountResponse;
    }

    if (response?.success) {
      fetchData();
      toast.success(response.message);
      setTab(1);
      resetForm();
      if ("account" in response && "scenario" in response) {
        setScenarioId([
          ...scenarioId,
          { accountId: response.account!.id, scenarioId: response.scenario!.id }
        ]);
      }
    } else {
      const errorMsg =
        response?.message ||
        (isEdit ? "Erro ao atualizar" : "Erro ao criar conta");
      toast.error(errorMsg);
    }
  };

  const resetForm = () => {
    reset(
      { name: "", id: 0 },
      { keepErrors: false, keepDirty: false, keepTouched: false }
    );
    setDocument("");
  };

  const resetNavigation = () =>
    setSearchParams({ pageAux: "1", page: "1" }, { replace: true });

  const onCancel = () => {
    resetNavigation();
    toast.warn("Alterações descartadas.");
    setIsEdit(false);
    resetForm();
    setTab(1);
  };

  /* ===== Funções de Edição e Exclusão de Contas ===== */

  const handleDeleteAccount = async (currentAccount: TDGKey) => {
    const response: TDeleteAccountResponse =
      await AccountsService.deleteAccount(currentAccount.id);
    if (response?.success) {
      await fetchData();
      toast.success(response.message);
      resetForm();
      setTab(1);
      const remainingAccounts = scenarioId.filter(
        (s) => s.accountId !== currentAccount.id
      );
      setScenarioId(remainingAccounts);
      setSelectedAccountCombo(remainingAccounts[0].accountId);
    } else {
      toast.error(response?.message ?? "Erro ao excluir a conta.");
    }
  };

  const handleEdit = async (accountParam: TDGKey) => {
    const rules = { shouldValidate: true, shouldDirty: true };
    setValue("name", accountParam.text ?? "", { ...rules });
    setValue("id", accountParam.id, { ...rules });
    await AccountsService.getAccountsTokens(
      1,
      10,
      Number(accountParam.id),
      "asc"
    );
    setIsEdit(true);
    setTab(2);
  };

  /* ===== Funções de Criação de Linhas para DataGrid ===== */
  const createRow = (accounts: TAccount[]): TDatagridData[] => {
    const filteredAccounts =
      role !== "owner"
        ? accounts.filter((user) => user.role !== "owner")
        : accounts;
    return filteredAccounts.map((s) => {
      let doc = "Sem registro";
      if (s.cnpj) {
        doc = cnpj.format(s.cnpj);
      } else if (s.cpf) {
        doc = cpf.format(s.cpf);
      }
      return {
        text: s.name,
        text2: doc,
        text4: s.credits ?? "Sem registro",
        id: s.id
      };
    });
  };

  /* ===== Função para Verificar se Botão deve ser Desabilitado ===== */
  const checkDisabled = () => {
    const name = watch("name");
    return isEdit ? name === "" : !name;
  };

  /* ===== Renderização ===== */
  return (
    <Page.Root>
      <Page.Header
        title="Contas"
        key="accounts"
        iconPage={<PeopleAltOutlinedIcon />}
        controlElement={[
          <ContentConditional.IsShow isShow={!isMobile()} key="search">
            <Page.Search
              placeholder="Busca por nome ou email"
              changeTextSearch={(texto) =>
                debounce(() => {
                  setSearchParams(
                    { busca: texto, pagina: "1" },
                    { replace: true }
                  );
                })
              }
            />
          </ContentConditional.IsShow>,
          <ContentConditional.Permission
            permission={["owner", "admin", "support"]}
            key="button"
          >
            <div style={{ width: "inherit" }}>
              <Page.Button
                label={
                  isMobile() ? "" : tab === 1 ? "Criar Conta" : "Listar contas"
                }
                borderColor={theme.colors.palete.gold}
                disabled={false}
                startIcon={tab === 1 ? <Add /> : <ViewListOutlined />}
                onClick={() => {
                  if (tab === 1) {
                    setTab(2);
                    setIsEdit(false);
                  } else {
                    resetForm();
                    setTab(1);
                  }
                }}
              />
            </div>
          </ContentConditional.Permission>
        ].filter(Boolean)}
      />

      {tab === 1 ? (
        <>
          <ScenarioContainer>
            <DatagridResponsive.Combo
              headers={[
                { field: "Nome" },
                { field: "Documento" },
                { field: "Créditos" },
                ...(role !== "collaborator"
                  ? [{ field: "Ações", width: "10vw" }]
                  : [])
              ]}
              data={createRow(accounts)}
              count={count}
              handleEdit={handleEdit}
              handleDelete={{
                modalTitle: "Deseja excluir a conta?",
                modalText: "Após a exclusão a conta não poderá mais ser usada.",
                onDelete: handleDeleteAccount
              }}
              navigateTable={navigateTable}
              rowHeader="text"
              page={page}
            />
          </ScenarioContainer>
        </>
      ) : (
        <>
          <HomeForm>
            <h2 className="title">
              {isEdit ? "Editar conta" : "Cadastro de conta"}
            </h2>
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
              <FormRow>
                {!isEdit && (
                  <Input.Masked
                    label="CNPJ"
                    type="CNPJ"
                    setValue={setDocument}
                    setIsValid={setIsDocValid}
                    isValid={isDocValid}
                    required
                  />
                )}
                <Input.FormLabel
                  name="name"
                  control={control}
                  label="Razão Social:"
                  placeholder=""
                  type="text"
                  rules={{ required: "O Nome é obrigatório" }}
                />
              </FormRow>
              <div className="btns-container">
                <Page.Button
                  label="Salvar"
                  type="submit"
                  disabled={checkDisabled()}
                />
                <Page.Button
                  label="Cancelar"
                  type="reset"
                  disabled={false}
                  onClick={onCancel}
                />
              </div>
            </form>
          </HomeForm>

          <ContentConditional.IsShow isShow={isEdit}>
            <ContentConditional.Permission permission={["owner", "support"]}>
              <TokensDisplay
                entity="accounts"
                accountId={watch("id").toString()}
              />
            </ContentConditional.Permission>
          </ContentConditional.IsShow>
        </>
      )}
    </Page.Root>
  );
};
